import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const INTEREST_TAGS = ["Desire", "Confidence", "Sensuality", "Kink & Fantasy", "Pleasure", "Connection", "Curiosity", "Body awareness"];

export default function BookingConsent() {
  const { booking_id } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({
    interests: [], boundaries: "", hard_limits: "",
    communication_preferences: "", anything_else: "", consent_acknowledged: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${booking_id}/consent`).then(r => {
      if (r.data?.consent?.interests) setForm({ ...form, ...r.data.consent });
    }).catch(() => {});
    api.get("/bookings").then(r => setBooking(r.data.find(b => b.booking_id === booking_id)));
    // eslint-disable-next-line
  }, [booking_id]);

  const toggle = (v) => setForm({ ...form, interests: form.interests.includes(v) ? form.interests.filter(x => x !== v) : [...form.interests, v] });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_acknowledged) return toast.error("Please acknowledge consent.");
    setSaving(true);
    try {
      await api.post(`/bookings/${booking_id}/consent`, form);
      toast.success("Consent recorded — thank you.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="booking-consent-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
      <div className="eyebrow-lg text-center">Boundaries &amp; Consent · This Session</div>
      <h1 className="mt-8 h-editorial text-5xl sm:text-6xl text-center">A private word <span className="italic-serif text-[#D4AF37]">before we meet.</span></h1>
      {booking && (
        <p className="mt-4 text-center text-sm text-zinc-500">{booking.city_name} · {new Date(booking.slot_iso).toLocaleString()}</p>
      )}
      <p className="mt-6 max-w-xl mx-auto text-center text-zinc-400 leading-relaxed">
        Consent is ongoing. This is your chance to shape the hour — you can update or withdraw any part of it at any time.
      </p>

      <form onSubmit={submit} className="mt-14 space-y-10">
        <div>
          <label className="eyebrow-lg block mb-4">What are you curious about this session?</label>
          <div className="flex flex-wrap gap-2">
            {INTEREST_TAGS.map(t => (
              <button type="button" data-testid={`consent-interest-${t.replace(/\s|&/g, "").toLowerCase()}`} key={t} onClick={() => toggle(t)}
                className={`px-4 py-2 rounded-full text-xs font-mono-eyebrow uppercase tracking-[0.24em] border transition-all ${form.interests.includes(t) ? "bg-[#0052FF] border-[#0052FF] text-white" : "border-white/15 text-zinc-400 hover:border-[#00D1FF]/50 hover:text-white"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="eyebrow-lg block mb-3">Boundaries you want honored</label>
          <textarea data-testid="consent-boundaries" rows={3} value={form.boundaries} onChange={e => setForm({...form, boundaries: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <div>
          <label className="eyebrow-lg block mb-3">Hard limits</label>
          <textarea data-testid="consent-hard-limits" rows={3} value={form.hard_limits} onChange={e => setForm({...form, hard_limits: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <div>
          <label className="eyebrow-lg block mb-3">Communication preferences</label>
          <textarea data-testid="consent-comm" rows={2} value={form.communication_preferences} onChange={e => setForm({...form, communication_preferences: e.target.value})} className="w-full px-4 py-3" placeholder="Verbal check-ins, safewords, pacing…" />
        </div>
        <div>
          <label className="eyebrow-lg block mb-3">Anything else you want me to know</label>
          <textarea data-testid="consent-anything" rows={3} value={form.anything_else} onChange={e => setForm({...form, anything_else: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <label className="flex gap-3 items-start text-sm text-zinc-300 leading-relaxed cursor-pointer">
          <input data-testid="consent-ack" type="checkbox" checked={form.consent_acknowledged} onChange={e => setForm({...form, consent_acknowledged: e.target.checked})} className="mt-1" />
          <span>I understand consent is ongoing and can be updated or withdrawn at any moment — before, during, or after our session. I am 21+.</span>
        </label>
        <button data-testid="consent-submit" type="submit" disabled={saving} className="w-full rc-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Confirm & Continue"}
        </button>
      </form>
    </div>
  );
}
